import { GridApi } from "@ag-grid-community/core";
import { GridApi as GridApiLegacy } from "ag-grid-community";
import axios from "axios";
import { countFilters, ReduxState, sleep, WINDOW_EVENT, hasArrayWithValue } from "helpers/helper";
import { camelCase, delay, get, isEmpty } from "lodash";
import {
  DependencyList,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { useSelector } from "react-redux";
import { useBarcodeScanner } from "./BarcodeScanner";
import { ICustomSRMForm } from "pages/settings/customFields/form/common";
import { SELECT_CONTRACT_TYPE } from "pages/tickets/ticketUtils";
import { FIELD_TYPE } from "components/commons/customizedfields/CustomizedFields";

export { useRequest } from "./request/useRequest";
export { useBarcodeScanner };

export function useForceUpdate() {
  const [state, setState] = useState(0);
  const forceUpdate = useCallback(() => {
    setState(state + 1);
  }, [state]);

  return [state, forceUpdate] as const;
}

type AddEventListener<Key> = Key extends keyof HTMLElementEventMap
  ? (e: HTMLElementEventMap[Key]) => any
  : (e: Event) => any;

export function useEventListener<EventKey extends keyof HTMLElementEventMap>(
  target: Window | HTMLElement | Document,
  event: EventKey,
  listener: AddEventListener<EventKey>,
  deps = []
) {
  useLayoutEffect(() => {
    if (!listener || !target) return;

    target.addEventListener(event, listener);
    return () => {
      target.removeEventListener(event, listener);
    };
  }, [target, ...deps]);
}

export type SetState<T> = (payload: Partial<T>) => void;

// TODO move to reducerHelpers.tsx
export function getSetState<S = any>(
  dispatch: React.Dispatch<{ type: "SET_STATE"; payload: Partial<S> }>
): (newState: Partial<S>) => void {
  return useCallback(
    payload => dispatch({ type: "SET_STATE", payload }),
    [dispatch]
  );
}

export function useOnResize(
  callback: (this: Window, ev: WindowEventMap[WINDOW_EVENT.RESIZE]) => any
) {
  useEffect(() => {
    window.addEventListener(WINDOW_EVENT.RESIZE, callback);
    return () => window.removeEventListener(WINDOW_EVENT.RESIZE, callback);
  }, [callback]);
}

export function useCompanyPermissions() {
  return useSelector<ReduxState, Models.CompanyPermissions>(
    state => state.auth.user?.permissions
  );
}

export function useUser() {
  return useSelector<ReduxState, Models.UserPermissions>(
    state => state.auth.user
  );
}

export function useDivisionsAndLocations(key?: Models.WarehousePermission) {
  const divisions = useSelector<ReduxState, Models.Warehouse[]>(
    state => state.auth?.user?.divisions || []
  );
  const locations = useSelector<ReduxState, Models.Warehouse[]>(
    state => state.auth?.user?.locations || []
  );
  const upgraded = useSelector<ReduxState, Boolean>(
    state => state.auth?.user?.upgraded
  );

  if (key) {
    return {
      divisions: divisions?.filter(e => e?.warehouse?.[key]) || [],
      locations: locations?.filter(e => e?.warehouse?.[key]) || [],
      upgraded
    };
  }
  return { divisions, locations, upgraded };
}

export function useCheckDivLocPermission(
  key: Models.UserWarehousePermission,
  checkLocations = false
) {
  const { divisions, locations, upgraded } = useDivisionsAndLocations();
  let locationPermission: boolean = true;
  if (checkLocations) {
    locationPermission =
      upgraded || locations.find(l => l.permissions[key]) !== undefined;
  }
  return divisions.find(d => d.permissions[key]) && locationPermission;
}

export function useCheckDivLocWarehouse(
  key: keyof Models.Warehouse["warehouse"] & any,
  division,
  location,
  checkLocations = false
) {
  const { divisions, locations, upgraded } = useDivisionsAndLocations();
  const findDivision = divisions.find(
    localDivision =>
      get(localDivision, `warehouse.${key}`, undefined) !== undefined &&
      get(localDivision, "warehouse.srm_uid", null) === division.trim()
  );
  if (!checkLocations) return get(findDivision, `warehouse.${key}`, false)

  const findLocation = locations.find(
    localLocation =>
      get(localLocation, `warehouse.${key}`, undefined) !== undefined &&
      get(localLocation, "warhouse.srm_uid", null) === location.trim()
  );
  const hasLocation = upgraded || Boolean(findLocation);
  return get(findDivision, `warehouse.${key}`, false) && hasLocation;
}

export function hasDivLocPermission(
  division: Models.Warehouse,
  location: Models.Warehouse,
  key: Models.UserWarehousePermission,
  warehouseKey?: Models.WarehousePermission
) {
  const upgraded = useSelector<ReduxState, boolean>(
    state => state?.auth?.user?.upgraded
  );
  const locationPermission = upgraded
    ? location?.is_user_location
    : location?.permissions?.[key];
  let permission = division?.permissions?.[key] && locationPermission;
  if (warehouseKey) {
    permission =
      permission &&
      division?.warehouse?.[warehouseKey] &&
      location?.warehouse?.[warehouseKey];
  }

  return permission;
}

export function useUserDivLocPermissions(
  key: Models.UserWarehousePermission,
  warehouseKey?: Models.WarehousePermission,
  promptDivision?: boolean,
  promptLocation?: boolean,
) {
  const { divisions, locations, upgraded } =
    useDivisionsAndLocations(warehouseKey);

  if (key) {
    return {
      divisions: divisions?.filter(e => {
        if (promptDivision === false) {
          return e?.is_doc_division && e?.permissions?.[key];
        }
        return e?.permissions?.[key];
      }),
      locations: upgraded
        ? locations?.filter(e => {
            if (promptLocation === false) {
              return e?.is_doc_location && e?.is_user_location;
            }
            return e?.is_user_location;
          })
        : locations?.filter(e => {
            if (promptLocation === false) {
              return e?.is_doc_location && e?.permissions?.[key];
            }
            if ([SELECT_CONTRACT_TYPE.DELIVERY, SELECT_CONTRACT_TYPE.PICKUP].includes(key as SELECT_CONTRACT_TYPE)){
              return true;
            }
            return e?.permissions?.[key];
          })
    };
  }
  return { divisions, locations };
}

export function useDefaultDivisionAndLocation(key, warehouseKey) {
  const { divisions, locations } = useUserDivLocPermissions(
    key,
    warehouseKey
  );

  const division = divisions?.find(e => e.is_doc_division)
  const location = locations?.find(e => e.is_doc_location && e.warehouse.srm_uid === division.warehouse.srm_uid)
  return {
    defaultDivision: division,
    defaultLocation: location || locations?.find(e => e.is_doc_location)
  }
}

export interface Option {
  label: string;
  value: any;
  timezone?: string;
}

export function useWarehouseOptions({
  isDivision = false,
  warehouses = [],
  userWarehousePermissionKey = "",
  warehousePermissionKey = "",
  key = "",
  subKey = "",
  timezone = false,
  docKey = ""
}: {
  isDivision: boolean,
  warehouses: Models.Warehouse[],
  userWarehousePermissionKey: Models.UserWarehousePermission | "",
  warehousePermissionKey: Models.WarehousePermission | "",
  key: keyof Models.Warehouse["warehouse"],
  subKey?: keyof Models.Warehouse["warehouse"],
  timezone?: boolean,
  docKey?: string
}) {
  const [options, setOptions] = useState([]);
  const upgraded = useSelector<ReduxState, boolean>(
    state => state.auth?.user?.upgraded
  );

  useEffect(() => {
    let newOptions: Models.Warehouse[] = warehouses;
    if (!isEmpty(userWarehousePermissionKey)) {
      newOptions = newOptions.filter(e =>
        isDivision || !upgraded
          ? e.permissions[userWarehousePermissionKey]
          : e.is_user_location
      );
    }
    if (!isEmpty(warehousePermissionKey)) {
      newOptions = newOptions.filter(e => e.warehouse[warehousePermissionKey]);
    }
    if (!isEmpty(docKey)) {
      newOptions = newOptions.filter(e => e[docKey] === true);
    }
    let mappedOptions: Option[] = newOptions?.map(
      ({ warehouse }: Models.Warehouse) => {
        let option: Option = {
          label: warehouse.title,
          value: warehouse[key] || " "
        };
        if (subKey) {
          option[subKey] = warehouse[subKey] || " ";
        }
        if (timezone) {
          option.timezone = warehouse.timezone;
        }
        return option;
      }
    );
    setOptions(mappedOptions);
  }, [warehouses, userWarehousePermissionKey, warehousePermissionKey, key, docKey]);

  return options as Option[];
}

export function useIsMounted() {
  const isMounted = useRef(false);
  useLayoutEffect(() => {
    isMounted.current = true;
  }, []);
  return isMounted;
}

export function useDidUnmount() {
  const unmounted = useRef(false);
  useEffect(
    () => () => {
      unmounted.current = true;
    },
    []
  );
  return unmounted;
}

export const useAsyncEffect = (
  callback: () => Promise<void>,
  deps?: DependencyList
) => {
  useEffect(() => {
    callback();
  }, deps);
};

/**
 * Async effect for handling axios cancel errors
 */
export const useRequestEffect: typeof useAsyncEffect = (callback, deps) => {
  useAsyncEffect(async () => {
    try {
      await callback();
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error(error);
    }
  }, deps);
};

export function useAgGridOverlayState(
  gridApi: GridApi | GridApiLegacy,
  isLoading: boolean
) {
  useEffect(() => {
    if (gridApi) {
      gridApi.hideOverlay();
    }
  }, [gridApi]);

  useEffect(() => {
    if (!gridApi) return;
    let canceled = false;

    if (isLoading) {
      gridApi.showLoadingOverlay();
    } else {
      const MAX_TIMEOUT = 200;
      let listener;

      // wait up to 200 milliseconds for the grid to update before resetting the overlay
      Promise.race([
        new Promise(resolve => {
          listener = resolve;
          gridApi.addEventListener("rowDataChanged", listener);
        }),
        sleep(MAX_TIMEOUT)
      ]).then(function resetOverlay() {
        gridApi.removeEventListener("rowDataChanged", listener);
        if (canceled) return;

        gridApi.hideOverlay();
        const showNoRowsOverlay = gridApi.getLastDisplayedRow() === -1;
        if (showNoRowsOverlay) gridApi.showNoRowsOverlay();
      });
    }

    return () => {
      canceled = true;
    };
  }, [gridApi, isLoading]);
}

export function useInterval(callback: Function, delay: number) {
  const savedCallback = useRef(null);

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export function useDidMount() {
  const [didMount, setDidMount] = useState(false);
  useLayoutEffect(() => {
    setDidMount(true);
  }, []);
  return didMount;
}

export function useKeyHack() {
  const keyMap = useRef(new WeakMap());
  const key = useRef(0);

  return function getKey(obj: object) {
    if (!keyMap.current.has(obj)) {
      keyMap.current.set(obj, ++key.current);
    }
    return keyMap.current.get(obj);
  };
}

export const usePrevious = <T,>({ value }: { value: any & T }) => {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

interface IUseFloatingFilter {
  state?: any;
  key?: string;
  setState?: ({ allFloatingFilter }: { [x: string]: boolean }) => void;
  allowedFilters?: Array<string>;
  columnApi: any;
  gridApi: any;
  columnDefs?: Array<any>;
  mapFiltersToFloatingFilters?: {
    [x: string]: string;
  };
}

export const useFloatingFilter = ({
  state,
  key = "allFloatingFilter",
  setState,
  columnApi,
  gridApi,
  columnDefs,
  allowedFilters = [
    "columnFilter",
    "dateRangeFilter",
    "postingTypeFilter",
    "optionFilter",
    "priorityFilter",
    "statusFilter",
    "flagFilter",
    "datefilter"
  ],
  mapFiltersToFloatingFilters = {
    columnFilter: "columnFloatingFilter"
  }
}: IUseFloatingFilter) => {
  const [floatingFilter, setFloatingFilter] = useState(false);
  const stateFromOutside = state && key ? state[key] : undefined;
  const outsideStateOrFloatingFilter =
    stateFromOutside !== undefined ? stateFromOutside : floatingFilter;
  const hasGridApi = gridApi !== undefined;
  const hasColumnApi = columnApi !== undefined;

  const toggleButtonState = useCallback(({ isLocal, key, value }) => {
    if (isLocal) {
      setFloatingFilter(!value);
    } else {
      setState({ [key]: !value });
    }
  }, []);

  const applyGridChanges = useCallback(
    ({ allow }: { allow: boolean }) => {
      if (columnApi && gridApi) {
        const newColumnState = columnApi.getColumnState();
        const updatedColumnDefs = columnDefs.map(item => {
          const filtered = get(item, "filter", "");
          const hasFilter = filtered ? true : false;
          const floatingFilterComponent = get(
            mapFiltersToFloatingFilters,
            filtered,
            {}
          );
          return {
            ...item,
            ...(allow && hasFilter && allowedFilters.includes(filtered)
              ? {
                  floatingFilter: true,
                  ...(floatingFilterComponent
                    ? { floatingFilterComponent }
                    : {})
                }
              : {})
          };
        });
        gridApi.setColumnDefs(updatedColumnDefs);
        columnApi.applyColumnState({
          state: newColumnState,
          applyOrder: true
        });
        delay(() => {
          gridApi.refreshCells();
        }, 1000);
      }
    },
    [hasColumnApi, hasGridApi]
  );

  const toggleFloatingFilter = useCallback(() => {
    toggleButtonState({
      isLocal: stateFromOutside === undefined && setState === undefined,
      key,
      value: outsideStateOrFloatingFilter
    });
    applyGridChanges({ allow: !outsideStateOrFloatingFilter });
  }, [outsideStateOrFloatingFilter, columnApi, gridApi]);

  const totalNumberOfFilters = countFilters({
    filters: {
      ...(state && state.filters ? state.filters : {}),
      ...(gridApi && gridApi.getFilterModel ? gridApi.getFilterModel() : {})
    }
  });

  useEffect(() => {
    if (hasColumnApi && hasGridApi) {
      applyGridChanges({ allow: outsideStateOrFloatingFilter });
    }
  }, [hasColumnApi, hasGridApi]);

  return {
    allFloatingFilter: outsideStateOrFloatingFilter,
    toggleFloatingFilter,
    countFilters,
    totalNumberOfFilters
  };
};

type TReturnUseToggle = {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  open: () => void;
  close: () => void;
} & any;

const useToggle = ({
  defaultShow = false,
  name
}: { defaultShow?: boolean; name?: string } = {}): TReturnUseToggle => {
  const [selectedData, setSelectedData] = useState<any>(null);
  const [show, setShow] = useState<boolean>(defaultShow);

  const open = (data?: any) => {
    if (data) setSelectedData(data);
    setShow(true);
  };
  const close = () => {
    setSelectedData(null);
    setShow(false);
  };
  const toggle = () => setShow(prevShow => !prevShow);

  return {
    show,
    setShow,
    open,
    close,
    selectedData,
    ...(name
      ? {
          [`${camelCase(`show_${name}`)}`]: show,
          [`${camelCase(`open_${name}`)}`]: open,
          [`${camelCase(`close_${name}`)}`]: close,
          [`${camelCase(`toggle_${name}`)}`]: toggle,
          [`${camelCase(`set_show_${name}`)}`]: setShow,
          [`${camelCase(`${name}_data`)}`]: selectedData
        }
      : {})
  };
};

export default useToggle;


export function getUDFsPostData(UDFs, docType) {
  return Object.values(UDFs)
    .filter((item: ICustomSRMForm) => {
      const field = item?.fields[0];
      const value = field?.value;
      if (!value && !hasArrayWithValue(value)) {
        return false;
      }
      return true;
    })
    .map((item: ICustomSRMForm) => {
      const field = item?.fields[0];
      const instruction = field?.instructions[0];
      const fieldType = field?.type;

      return {
        ...item,
        fields: [
          {
            ...field,
            ...(fieldType === FIELD_TYPE.MULTISELECT
              ? {
                  value: field?.value?.map(item => item?.value)
                }
              : {
                  value: [field?.value]
                }),
            ...("selections" in field
              ? {
                  selections: field?.selections?.map(item => {
                    return {
                      select: item?.select
                    };
                  })
                }
              : {}),
            instructions: [
              {
                ...instruction,
                docType
              }
            ]
          }
        ]
      };
    });
}
